import Button from '@carvana/ds-button';
import Users from '@carvana/ds-icons/UsersCvna';
import LinkButton from '@carvana/ds-link-button';
import { icon } from '@carvana/ds-styles';
import Switch from '@carvana/ds-switch';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useState } from 'react';
import FinanceCard from '~/components/flp/Prequalified/CustomizeYourDeal/FinanceCard';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import { useModalContext } from '~/context/flp/ModalContext/ModalContext';
import useToggleCoSigner from '~/hooks/flp/services/useToggleCoSigner';
import {
  Avatar,
  CoSignerContent,
  CoSignerDetails,
  Initials,
  Name
} from './CoSigner.styles';

const textOptions = {
  default: 'Adding a co-signer can reduce your minimum cash down.',
  enabled: 'Terms include your co-signer.',
  disabled: "Terms don't include your co-signer.",
  error:
    'We are having trouble retrieving your co-signer information. This will not be reflected in your pricing.'
};

const CoSigner = () => {
  const { isLoading, coSigner } = useFinancingContext();
  const { data: coSignerData, error, enabled } = coSigner || {};
  const { setAddCoApplicantModalOpen, setEditCoApplicantModalOpen } =
    useModalContext();
  const { trackEvent } = useContext(AnalyticsContext);
  const toggleCoSigner = useToggleCoSigner();

  const [isToggling, setIsToggling] = useState<boolean>(false);
  const [switchState, setSwitchState] = useState<boolean>(enabled || false);

  const handleAddCosigner = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_ADD_COSIGNER);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_ADD_COSIGNER);
    setAddCoApplicantModalOpen(true);
  };

  const handleEditCoSigner = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_EDIT_COSIGNER);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_EDIT_COSIGNER);
    setEditCoApplicantModalOpen(true);
  };

  const handleToggleCosigner = async () => {
    if (isToggling) return;
    setIsToggling(true);
    const currentStatus = switchState;
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.TOGGLES_COSIGNER, {
      toggleAction: !switchState
    });
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.TOGGLES_COSIGNER, {
      toggleAction: !switchState
    });
    try {
      setSwitchState(!currentStatus);
      await toggleCoSigner();
    } catch (err) {
      setSwitchState(currentStatus);
    } finally {
      setIsToggling(false);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      setSwitchState(Boolean(enabled));
    }
  }, [isLoading, enabled]);

  // NOTE(2024/08): Customer is not eligible for Co-Signer. Do not display Co-Signer section.
  if (coSigner === null) {
    return null;
  }

  const hasError = Boolean(error);
  const hasCosigner = Boolean(coSignerData?.firstName);
  const coSignerInitials = hasCosigner
    ? `${coSignerData?.firstName[0]}${coSignerData?.lastName[0]}`
    : '';

  const CardToggle = () => {
    if (!hasCosigner || hasError) return null;

    return (
      <Switch
        aria-label="Include co-signer in terms"
        checked={switchState}
        disabled={isLoading || isToggling}
        onChange={handleToggleCosigner}
      />
    );
  };

  const text = (() => {
    switch (true) {
      case hasError:
        return textOptions.error;
      case enabled:
        return textOptions.enabled;
      case !enabled && hasCosigner:
        return textOptions.disabled;
      default:
        return textOptions.default;
    }
  })();

  return (
    <FinanceCard
      heading="Co-signer"
      id="co-signer"
      icon={<Users color={icon.primary} size={24} />}
      text={text}
      disabled={hasError}
      showDivider={hasCosigner}
      Switch={<CardToggle />}
    >
      {hasCosigner ? (
        <CoSignerContent>
          <Avatar>
            <Initials>{coSignerInitials}</Initials>
          </Avatar>

          <CoSignerDetails>
            <Name>{`${coSignerData?.firstName} ${coSignerData?.lastName}`}</Name>
            <LinkButton
              data-analytics-id="Financing Co-Signer Edit Link"
              disabled={isLoading}
              size="sm"
              onClick={handleEditCoSigner}
              data-testid="edit-co-signer"
            >
              Edit
            </LinkButton>
          </CoSignerDetails>
        </CoSignerContent>
      ) : (
        !hasError && (
          <Button
            className="self-start"
            data-analytics-id="Financing Co-Signer Add Button"
            size="sm"
            variant="secondary"
            onClick={handleAddCosigner}
            data-testid="add-co-signer"
          >
            Add a Co-signer
          </Button>
        )
      )}
    </FinanceCard>
  );
};

export default CoSigner;
